import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageProvider";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://app-api.wonyasoft.com"
).replace(/\/$/, "");

const contentCache = new Map();

function localizedValue(item, field, language) {
  const preferred = item[`${field}${language === "en" ? "En" : "Fr"}`];
  const fallback = item[`${field}${language === "en" ? "Fr" : "En"}`];
  return preferred || fallback || "";
}

function sameOriginMediaUrl(mediaUrl) {
  if (!mediaUrl || typeof window === "undefined") {
    return mediaUrl;
  }

  try {
    const url = new URL(mediaUrl, window.location.origin);
    if (url.pathname.startsWith("/uploads/site-content/")) {
      return `${window.location.origin}${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Keep the API value when a legacy/custom media URL is not a valid URL.
  }

  return mediaUrl;
}

function optimizedImageUrl(mediaUrl) {
  if (!mediaUrl || !/\.(?:png|jpe?g)(?=$|[?#])/i.test(mediaUrl)) {
    return mediaUrl;
  }

  return mediaUrl.replace(/\.(png|jpe?g)(?=$|[?#])/i, ".webp");
}

function ManagedMedia({ item, title }) {
  const originalMediaUrl = sameOriginMediaUrl(item.mediaUrl || "");
  const [mediaUrl, setMediaUrl] = useState(() =>
    optimizedImageUrl(originalMediaUrl),
  );

  useEffect(() => {
    setMediaUrl(optimizedImageUrl(originalMediaUrl));
  }, [originalMediaUrl]);

  if (!originalMediaUrl) {
    return null;
  }

  if (item.mediaType === "video") {
    return (
      <video
        className="ws-managed-content__media"
        controls
        preload="metadata"
        aria-label={title}
      >
        <source src={item.mediaUrl} />
      </video>
    );
  }

  return (
    <img
      className="ws-managed-content__media"
      src={mediaUrl}
      alt={title}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (mediaUrl !== originalMediaUrl) {
          setMediaUrl(originalMediaUrl);
        }
      }}
    />
  );
}

export default function HomeManagedContent({ section }) {
  const { language } = useLanguage();
  const [content, setContent] = useState(
    () => contentCache.get(section) || null,
  );

  useEffect(() => {
    if (process.env.NODE_ENV === "test") {
      return undefined;
    }

    const controller = new AbortController();

    fetch(`${API_BASE_URL}/public/site-content/${section}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Content request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((response) => {
        if (response?.success && response.data) {
          contentCache.set(section, response.data);
          setContent(response.data);
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setContent(null);
        }
      });

    return () => controller.abort();
  }, [section]);

  if (!content?.enabled || !content.items?.length) {
    return null;
  }

  const sectionTitle =
    language === "en"
      ? content.titleEn || content.titleFr
      : content.titleFr || content.titleEn;
  const isTestimonials = section === "testimonials";
  const visibleItems = isTestimonials
    ? content.items
    : [...content.items]
        .sort(
          (first, second) =>
            new Date(second.createdAt || 0).getTime() -
            new Date(first.createdAt || 0).getTime(),
        )
        .slice(0, 3);

  return (
    <section
      className={`ws-managed-content ws-managed-content--${section}`}
      aria-labelledby={`managed-${section}-title`}
    >
      <div className="ws-shell">
        <header className="ws-managed-content__header">
          <span className="ws-kicker">
            {isTestimonials
              ? language === "en"
                ? "CUSTOMER STORIES"
                : "TÉMOIGNAGES"
              : language === "en"
                ? "WONYA NEWS"
                : "ACTUALITÉS WONYA"}
          </span>
          <h2 id={`managed-${section}-title`}>{sectionTitle}</h2>
        </header>

        <div
          className={`ws-managed-content__grid ${
            isTestimonials ? "is-testimonials" : "is-blog"
          }`}
        >
          {visibleItems.map((item) => {
            const title = localizedValue(item, "title", language);
            const subtitle = localizedValue(item, "subtitle", language);
            const body = localizedValue(item, "body", language);

            return (
              <article className="ws-managed-content__card" key={item.id}>
                <ManagedMedia item={item} title={title} />
                <div className="ws-managed-content__body">
                  {subtitle && (
                    <span className="ws-managed-content__subtitle">
                      {subtitle}
                    </span>
                  )}
                  <h3>{title}</h3>
                  {body &&
                    (isTestimonials ? (
                      <blockquote>{body}</blockquote>
                    ) : (
                      <>
                        <p className="ws-managed-content__excerpt">{body}</p>
                        <Link
                          className="ws-managed-content__link"
                          to={`/blog/${encodeURIComponent(item.id)}`}
                          aria-label={`${language === "en" ? "Read article" : "Lire l’article"} : ${title}`}
                        >
                          {language === "en"
                            ? "Read article"
                            : "Lire l’article"}
                          <span aria-hidden="true">→</span>
                        </Link>
                      </>
                    ))}
                </div>
              </article>
            );
          })}
        </div>
        <div className="ws-managed-content__archive-link">
          <Link
            className="ws-btn ws-btn-secondary"
            to={isTestimonials ? "/testimonials" : "/blog"}
          >
            {isTestimonials
              ? language === "en"
                ? "View all testimonials"
                : "Voir tous les témoignages"
              : language === "en"
                ? "View all articles"
                : "Voir tous les articles"}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
